import { z } from 'zod';

import { SHELF_TEMPERATURES } from '@constants/index';

import { apiSuccess, apiList } from '@services/apiSchemas';

import adminApiFetch from './apiService';
import { productCategoryService } from './productCategoryService';

// ---- Schemas (mirror app/controllers/api/admin/v1/products_controller.rb) ----

// `index` — autocomplete dropdown (controller L35-37, narrow as_json projection).
const ProductSearchItemSchema = z
  .object({
    id: z.string(),
    sku: z.string(),
    name: z.string(),
    validity_days: z.number().nullable(),
    manufacturing_date_required: z.boolean(),
    batch_management_required: z.boolean(),
  })
  .passthrough();

// `get_records` — table view with aggregated stocks (controller L49-73).
const ProductTableRowSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    brand: z.string().nullable(),
    barcode: z.string().nullable(),
    stocks: z
      .object({
        total: z.number(),
        available: z.number(),
      })
      .passthrough(),
    created_at: z.string(),
    activated: z.boolean(),
    last_inbound_date: z.string().nullable(),
    last_outbound_date: z.string().nullable(),
  })
  .passthrough();

// `show` — full record (controller L133-160). The exact column set is a moving
// target so we type the named fields and let extras pass through.
const ProductDetailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable(),
    activated: z.boolean(),
    editable: z.boolean(),
    deletable: z.boolean(),
    batch_management_required: z.boolean(),
    manufacturing_date_required: z.boolean(),
    secondary_barcodes: z.array(z.string()).optional(),
    public_photo_url: z.string().nullable().optional(),
    brand: z
      .object({ id: z.string(), name: z.string() })
      .passthrough()
      .nullable()
      .optional(),
    product_category: z
      .object({ id: z.string(), name: z.string() })
      .passthrough()
      .nullable()
      .optional(),
    product_substitutes: z.array(z.unknown()).optional(),
    product_edit_histories: z.array(z.unknown()).optional(),
    product_attribute_definitions: z.array(z.unknown()).optional(),
  })
  .passthrough();

// `create` / `update` — `render_data_item(product)` returns the AR record's
// default `as_json` (full column set, no eager-loaded relations on this branch).
const ProductSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable(),
    activated: z.boolean(),
  })
  .passthrough();

// `check_sku` — branched: { exists: true, id, name } OR { exists: false }.
const CheckSkuSchema = z.discriminatedUnion('exists', [
  z.object({ exists: z.literal(true), id: z.string(), name: z.string() }),
  z.object({ exists: z.literal(false) }),
]);

// `in_transit` — array of inbound-item rows (controller L340-352).
const InTransitItemSchema = z
  .object({
    id: z.string(),
    inbound_id: z.string(),
    inbound_name: z.string().nullable(),
    inbound_status: z.string(),
    eta: z.string().nullable(),
    batch: z.string().nullable(),
    expiration_date: z.string().nullable(),
    scheduled_quantity: z.number(),
    putaway_quantity: z.number(),
    inventory_type: z.string().nullable(),
  })
  .passthrough();

// `serial_numbers#get_records` — 出貨序號列表（mirror
// app/controllers/api/admin/v1/products/serial_numbers_controller.rb get_records）。
// order_name / order_id / shipped_at 在序號尚未出貨時為 null。
const SerialNumberRecordSchema = z
  .object({
    id: z.string(),
    serial_number: z.string(),
    order_name: z.string().nullable(),
    order_id: z.string().nullable(),
    shipped_at: z.string().nullable(),
    created_at: z.string(),
  })
  .passthrough();

// Brand list — `brands_controller#index` returns AR records with at least
// `id` + `name`; we pluck those two fields below.
const BrandRecordSchema = z
  .object({ id: z.string(), name: z.string() })
  .passthrough();

const IdsListSchema = z.array(z.string());
const JobIdSchema = z.object({ job_id: z.string() }).passthrough();
const ImportJobSchema = z
  .object({ job_id: z.string(), import_id: z.string() })
  .passthrough();
const EmptyDataSchema = z.unknown();
// Generic schemas for endpoints whose payload shape isn't load-bearing for
// frontend logic (preview/replace SKU, attribute defs, label generation).
const LooseDataSchema = z.unknown();

// ---- Type exports ---------------------------------------------------------

export type ProductSearchItem = z.infer<typeof ProductSearchItemSchema>;
export type ProductTableRow = z.infer<typeof ProductTableRowSchema>;
export type ProductDetail = z.infer<typeof ProductDetailSchema>;
export type Product = z.infer<typeof ProductSchema>;
export type CheckSkuResult = z.infer<typeof CheckSkuSchema>;
export type InTransitItem = z.infer<typeof InTransitItemSchema>;

// ---- Input types ----------------------------------------------------------

interface FetchListParams {
  search?: string;
  limit?: number;
  merchant_id?: string;
}

interface CheckSkuOptions {
  productId?: string;
}

interface BrandOption {
  label: string;
  value: string;
  name: string;
  id: string;
}

interface CategoryOption {
  label: string;
  value: string;
}

// ---- Service --------------------------------------------------------------

const productService = {
  // 獲取產品列表（用於搜尋）
  fetchList: async (params: FetchListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.merchant_id) queryParams.append('merchant_id', params.merchant_id);

    const res = await adminApiFetch(`products?${queryParams.toString()}`, {
      method: 'GET',
    });
    return apiList(ProductSearchItemSchema).parse(await res.json());
  },

  // 獲取產品列表（用於表格）
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch('products/get_records', {
      method: 'POST',
      body: params,
    });
    return apiList(ProductTableRowSchema).parse(await res.json());
  },

  // 獲取出貨序號列表（產品詳情頁「出貨序號列表」抽屜）
  getSerialNumbers: async (productId: string, params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch(`products/${productId}/serial_numbers/get_records`, {
      method: 'POST',
      body: params,
    });
    return apiList(SerialNumberRecordSchema).parse(await res.json());
  },

  // 獲取所有產品 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch('products/get_ids', {
      method: 'POST',
      body: params,
    });
    return apiSuccess(IdsListSchema).parse(await res.json());
  },

  // 獲取單個產品
  get: async (id: string) => {
    const res = await adminApiFetch(`products/${id}`, {
      method: 'GET',
    });
    return apiSuccess(ProductDetailSchema).parse(await res.json());
  },

  // 獲取單個產品（別名）
  getById: async (id: string) => {
    return productService.get(id);
  },

  // 創建產品
  create: async (data: Record<string, unknown>) => {
    const res = await adminApiFetch('products', {
      method: 'POST',
      body: { product: data },
    });
    return apiSuccess(ProductSchema).parse(await res.json());
  },

  // 更新產品
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await adminApiFetch(`products/${id}`, {
      method: 'PUT',
      body: { product: data },
    });
    return apiSuccess(ProductSchema).parse(await res.json());
  },

  // 刪除產品
  delete: async (id: string) => {
    const res = await adminApiFetch(`products/${id}`, {
      method: 'DELETE',
    });
    return apiSuccess(EmptyDataSchema).parse(await res.json());
  },

  // 檢查品號是否可用
  // options: { productId } (編輯模式排除自身)
  checkSku: async (sku: string, options: CheckSkuOptions = {}) => {
    const params = new URLSearchParams({ sku });
    if (options.productId) {
      params.append('product_id', options.productId);
    }
    const res = await adminApiFetch(`products/check_sku?${params.toString()}`, {
      method: 'GET',
    });
    return apiSuccess(CheckSkuSchema).parse(await res.json());
  },

  // 獲取品牌列表
  getBrands: async (): Promise<BrandOption[]> => {
    const res = await adminApiFetch('brands', {
      method: 'GET',
    });
    const data = apiList(BrandRecordSchema).parse(await res.json());
    if (data.success && Array.isArray(data.data)) {
      return data.data.map(brand => ({
        label: brand.name,
        value: brand.id,
        name: brand.name,
        id: brand.id,
      }));
    }
    return [];
  },

  // 獲取溫層選項（從 constants 獲取，需搭配 i18n 翻譯）
  getTemperatures: async () => {
    return SHELF_TEMPERATURES;
  },

  // 獲取分類選項（organization 層級，委託給 productCategoryService）
  getCategories: async (): Promise<CategoryOption[]> => {
    const data = await productCategoryService.fetchList();
    if (data.success && Array.isArray(data.data)) {
      return data.data.map((c: { name: string; id: string }) => ({
        label: c.name,
        value: c.id,
      }));
    }
    return [];
  },

  // 匯出產品
  export: async (productIds: string[]) => {
    const res = await adminApiFetch('products/export', {
      method: 'POST',
      body: { ids: productIds },
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 獲取在途庫存
  fetchInTransit: async (id: string) => {
    const res = await adminApiFetch(`products/${id}/in_transit`, {
      method: 'GET',
    });
    return apiSuccess(z.array(InTransitItemSchema)).parse(await res.json());
  },

  // 下載匯入模板
  downloadImportTemplate: async (locale: string) => {
    return adminApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify({ type: 'product', locale }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // 期望返回 Excel
      },
    });
  },

  // 匯入產品
  import: async (formData: FormData) => {
    const res = await adminApiFetch('products/import', {
      method: 'POST',
      body: formData,
    });
    return apiSuccess(ImportJobSchema).parse(await res.json());
  },

  // 批量更新
  batchUpdate: async (selectedIds: string[], actions: Record<string, unknown>) => {
    const res = await adminApiFetch('products/batch_update', {
      method: 'POST',
      body: {
        ids: selectedIds,
        actions: actions,
      },
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 生成產品標籤
  generateLabel: async (productId: string, templateId: string, copies: number) => {
    const res = await adminApiFetch(`products/${productId}/generate_label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: templateId,
        copies: copies,
      }),
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 獲取產品自定義屬性定義
  getProductAttributeDefinitions: async () => {
    const res = await adminApiFetch('product_attribute_definitions', {
      method: 'GET',
    });
    return apiList(LooseDataSchema).parse(await res.json());
  },

  // 預覽更換 SKU
  previewReplaceSku: async (data: Record<string, unknown>) => {
    const res = await adminApiFetch('products/preview_replace_sku', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return apiSuccess(LooseDataSchema).parse(await res.json());
  },

  // 執行更換 SKU
  replaceSku: async (data: Record<string, unknown>) => {
    const res = await adminApiFetch('products/replace_sku', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return apiSuccess(LooseDataSchema).parse(await res.json());
  },
};

export { productService };
export default productService;
